import { ArrowLeft } from '@phosphor-icons/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, type ReactNode } from 'react';

import { SearchField } from '@shared/ui/SearchField';
import styles from './StoreSearchOverlay.module.css';

interface StoreSearchOverlayProps {
  open: boolean;
  query: string;
  onChange: (query: string) => void;
  onClose: () => void;
  children: ReactNode;
}

/** Owns the modal search surface, keyboard timing, and page scroll lock. */
export function StoreSearchOverlay({
  open,
  query,
  onChange,
  onClose,
  children,
}: StoreSearchOverlayProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus after the emphasized morph settles enough that the mobile keyboard
  // does not resize the viewport during Framer's layout measurement.
  useEffect(() => {
    if (!open) return;
    const timeout = window.setTimeout(
      () => inputRef.current?.focus({ preventScroll: true }),
      240,
    );
    return () => window.clearTimeout(timeout);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const close = () => {
    inputRef.current?.blur();
    onClose();
  };

  return (
    <AnimatePresence initial={false}>
      {open ? (
        <motion.div
          className={styles.page}
          role="dialog"
          aria-modal="true"
          aria-label="Search products"
          initial={false}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.12 }}
        >
          <div className={styles.bar}>
            <motion.button
              type="button"
              className={styles.back}
              aria-label="Close search"
              initial={{ opacity: 0, x: 8, scale: 0.88 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ delay: 0.12, duration: 0.2, ease: [0.2, 0, 0, 1] }}
              onClick={close}
            >
              <ArrowLeft size={20} weight="bold" />
            </motion.button>
            <SearchField
              query={query}
              onChange={onChange}
              expanded
              inputRef={inputRef}
              layoutId="store-search-bar"
              placeholder="Search products and services"
              label="Search products"
            />
          </div>

          <motion.div
            className={styles.content}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.14, duration: 0.24, ease: [0.2, 0, 0, 1] }}
          >
            <p className={styles.resultsLabel}>
              {query.trim() ? `Results for “${query.trim()}”` : 'All products'}
            </p>
            {children}
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
